
import { StateCreator } from 'zustand';
import { UserState } from '@/store/slices/auth/userSlice';
import { DealerState } from './types/stateTypes';
import { transformDealersData } from './dealerTransformers';
import { 
  fetchDealersWithPrices,
  fetchDealersData 
} from './dealers/dealersActions';
import { 
  fetchFavorites,
  addToFavorites, 
  removeFromFavorites 
} from './favorites/favoritesActions';
import { toast } from 'sonner';
import { validateAuthenticationWithExplicitId } from '@/utils/authValidation';

type StoreWithUser = DealerState & UserState;

export const createDealerSlice: StateCreator<
  StoreWithUser,
  [],
  [],
  DealerState
> = (set, get) => ({
  dealers: [],
  favoritesDealers: [],
  
  fetchDealers: async () => {
    try {
      const { dealersData, latestPrices } = await fetchDealersWithPrices();
      const favoritesDealers = get().favoritesDealers;
      const dealers = transformDealersData(dealersData, favoritesDealers, latestPrices);
      set({ dealers });
    } catch (error) {
      console.error('Error fetching dealers:', error);
    }
  },
  
  fetchFavoriteDealers: async () => {
    const { user, isAuthenticated } = get();
    
    const { isValid, userId } = validateAuthenticationWithExplicitId(
      isAuthenticated, 
      user, 
      undefined
    );
    
    if (!userId) {
      console.log("Valid user ID not found in fetchFavoriteDealers, clearing favorites");
      set({ favoritesDealers: [] });
      return [];
    }
    
    try {
      const favoriteIds = await fetchFavorites(userId);
      set({ favoritesDealers: favoriteIds });
      
      const dealers = get().dealers.map(dealer => ({
        ...dealer,
        isFavorite: favoriteIds.includes(dealer.id)
      }));
      
      set({ dealers });
      return favoriteIds;
    } catch (error) {
      console.error('Error fetching favorite dealers:', error);
      set({ favoritesDealers: [] });
      return [];
    }
  },
  
  toggleFavoriteDealer: async (dealerId, explicitUserId) => {
    const { favoritesDealers, user, isAuthenticated } = get();
    
    const { isValid, userId } = validateAuthenticationWithExplicitId(
      isAuthenticated, 
      user, 
      explicitUserId
    );
    
    if (!userId) {
      toast.error("Autenticazione richiesta", {
        description: "Per gestire i preferiti, devi essere autenticato correttamente."
      });
      return;
    }
    
    const isFavorite = favoritesDealers.includes(dealerId);
    
    try {
      if (isFavorite) {
        await removeFromFavorites(userId, dealerId);
      } else {
        await addToFavorites(userId, dealerId);
      }
      
      const newFavorites = isFavorite
        ? favoritesDealers.filter(id => id !== dealerId)
        : [...favoritesDealers, dealerId];
      
      set({ 
        favoritesDealers: newFavorites,
        dealers: get().dealers.map(d => 
          d.id === dealerId 
            ? { ...d, isFavorite: !isFavorite } 
            : d
        ) 
      });
      
    } catch (error) {
      console.error('Error toggling favorite dealer:', error);
      
      const errorMessage = (error as any)?.message || '';
      const isDuplicateError = errorMessage.includes('duplicate key') || 
                             (error as any)?.code === '23505';
      
      if (!isDuplicateError) {
        await get().fetchFavoriteDealers();
      }
    }
  },
});
